import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import menuConfig from "./menuconfig";

const NavLinks = ({ loggedIn, role, isActive, dashboardPath }) => {
  const [openDropdown, setOpenDropdown] = useState(null);
  const closeTimerRef = useRef(null);
  const wrapperRef = useRef(null);

  const handleToggle = (label) => {
    // click toggles immediately (works for both desktop and mobile)
    clearCloseTimer();
    setOpenDropdown((prev) => (prev === label ? null : label));
  };

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = (delay = 150) => {
    // small delay to avoid accidental closing when moving pointer
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setOpenDropdown(null);
      closeTimerRef.current = null;
    }, delay);
  };

  const handleMouseEnter = (label) => {
    // only apply hover open on wider screens (desktop)
    if (window.innerWidth > 768) {
      clearCloseTimer();
      setOpenDropdown(label);
    }
  };

  const handleMouseLeave = () => {
    if (window.innerWidth > 768) {
      scheduleClose(150);
    }
  };

  // close when clicking outside
  useEffect(() => {
    function onDocClick(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpenDropdown(null);
      }
    }
    function onKey(e) {
      if (e.key === "Escape") setOpenDropdown(null);
    }
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // cleanup timer on unmount
  useEffect(() => {
    return () => clearCloseTimer();
  }, []);

  const renderChildren = (children) =>
    children.map((child) => (
      <li
        key={child.to}
        className={isActive(child.to) ? "active-link" : ""}
        onClick={() => setOpenDropdown(null)} // close menu after click
      >
        <Link to={child.to}>{child.label}</Link>
      </li>
    ));

  if (loggedIn) {
    const menuItems = menuConfig[role] || [];

    return (
      <div ref={wrapperRef} className="navlinks-wrapper">
        {menuItems.map((item) =>
          item.children ? (
            <li
              key={item.label}
              className={`dropdown ${openDropdown === item.label ? "open" : ""}`}
              onMouseEnter={() => handleMouseEnter(item.label)}
              onMouseLeave={handleMouseLeave}
            >
              <span
                className="dropdown-trigger"
                onClick={() => handleToggle(item.label)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") handleToggle(item.label);
                }}
              >
                {item.label}{" "}
                <span className={`arrow ${openDropdown === item.label ? "open" : ""}`}>▼</span>
              </span>

              <ul className={`dropdown-menu ${openDropdown === item.label ? "show" : ""}`}>
                {renderChildren(item.children)}
              </ul>
            </li>
          ) : (
            <li key={item.to} className={isActive(item.to) ? "active-link" : ""}>
              <Link to={item.to}>{item.label}</Link>
            </li>
          )
        )}
      </div>
    );
  }

  // Public menu for non-logged-in users
return (
  <div ref={wrapperRef} className="navlinks-wrapper">
    <ul className="navbar-links">
      <li className={isActive("/") ? "active-link" : ""}>
        <Link to="/">Home</Link>
      </li>

      <li className={isActive("/about") ? "active-link" : ""}>
        <Link to="/about">About Us</Link>
      </li>

      <li className={isActive("/eligibility") ? "active-link" : ""}>
        <Link to="/eligibility">Check Est. Eligibility</Link>
      </li>

      <li
        className={`dropdown ${openDropdown === "Loans" ? "open" : ""}`}
        onMouseEnter={() => handleMouseEnter("Loans")}
        onMouseLeave={handleMouseLeave}
      >
        <span
          className="dropdown-trigger"
          onClick={() => handleToggle("Loans")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleToggle("Loans");
          }}
        >
          Loans <span className={`arrow ${openDropdown === "Loans" ? "open" : ""}`}>▼</span>
        </span>

        <ul className={`dropdown-menu ${openDropdown === "Loans" ? "show" : ""}`}>
          <li onClick={() => setOpenDropdown(null)}>
            <Link to="/personal-loan">Personal Loan</Link>
          </li>
          <li onClick={() => setOpenDropdown(null)}>
            <Link to="/vehicle-loan">Vehicle Loan</Link>
          </li>
          <li onClick={() => setOpenDropdown(null)}>
            <Link to="/business-loan">Business Loan</Link>
          </li>
          <li onClick={() => setOpenDropdown(null)}>
            <Link to="/home-loan">Home Loan</Link>
          </li>
        </ul>
      </li>

      <li className={isActive("/company-list") ? "active-link" : ""}>
        <Link to="/company-list">Company List</Link>
      </li>

      <li
        className={`dropdown ${openDropdown === "Useful Links" ? "open" : ""}`}
        onMouseEnter={() => handleMouseEnter("Useful Links")}
        onMouseLeave={handleMouseLeave}
      >
        <span
          className="dropdown-trigger"
          onClick={() => handleToggle("Useful Links")}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") handleToggle("Useful Links");
          }}
        >
          Useful Links <span className={`arrow ${openDropdown === "Useful Links" ? "open" : ""}`}>▼</span>
        </span>

        <ul className={`dropdown-menu ${openDropdown === "Useful Links" ? "show" : ""}`}>
          <li onClick={() => setOpenDropdown(null)}>
            <Link to="/contact">Contact Us</Link>
          </li>

          <li onClick={() => setOpenDropdown(null)}>
            <Link to="/careers">Careers</Link>
          </li>
          <li onClick={() => setOpenDropdown(null)}>
            <Link to="/consent-policy">Consent Policy</Link>
          </li>
          <li onClick={() => setOpenDropdown(null)}>
            <Link to="/terms">Terms & Conditions</Link>
          </li>
          <li onClick={() => setOpenDropdown(null)}>
            <Link to="/privacy">Privacy Policy</Link>
          </li>
        </ul>
      </li>
    </ul>
  </div>
);
};

export default NavLinks;